import { useRef, useState } from "react";
import { api, type ListerSession } from "../../../lib/api";
import { downscaleToJpeg } from "../../../lib/image";

const MAX_PER_UPLOAD = 8;

/** Operator photographs for a session whose research found no usable images — shop supplies,
 *  accessories, local promos and anything else the web has no stock photo of. Two separate
 *  controls on purpose: `capture` opens the camera directly on a phone, which is what you want
 *  standing at the counter, but it also REMOVES the choice of picking an existing file. One
 *  input cannot offer both, so there are two. */
export default function AddPhotos({ sessionId, empty, disabled, onAdded }: {
  sessionId: string;
  /** Research produced nothing — promote this from a quiet link to the obvious next action. */
  empty: boolean;
  disabled?: boolean;
  onAdded: (session: ListerSession) => void;
}) {
  const camera = useRef<HTMLInputElement>(null);
  const library = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const take = async (files: FileList | null, input: HTMLInputElement | null) => {
    if (!files || files.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const shots = await Promise.all(Array.from(files).slice(0, MAX_PER_UPLOAD).map((f) => downscaleToJpeg(f, 1600)));
      onAdded(await api.addListerSessionPhotos(sessionId, shots.map((s) => s.b64)));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      // Re-selecting the SAME file must fire change again, so clear the input either way.
      if (input) input.value = "";
    }
  };

  const btn = "px-3 py-2 rounded-md border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 disabled:opacity-50 text-sm";
  return (
    <div className={empty ? "border border-amber-700 bg-amber-950/30 rounded-md p-3 space-y-2" : "space-y-2 pt-2"}>
      {empty && (
        <p className="text-amber-200 text-sm">
          No pictures were found for this item. Take one or upload one — a listing cannot be created without at least one image.
        </p>
      )}
      <div className="flex flex-wrap gap-2 items-center">
        <button type="button" className={btn} disabled={disabled || busy} onClick={() => camera.current?.click()}>
          {busy ? "uploading…" : "Take photo"}
        </button>
        <button type="button" className={btn} disabled={disabled || busy} onClick={() => library.current?.click()}>
          Upload photo
        </button>
        {!empty && <span className="text-zinc-500 text-xs">adds to the images above</span>}
      </div>
      <input ref={camera} type="file" accept="image/*" capture="environment" className="hidden"
             aria-label="take a photo of this product"
             onChange={(e) => void take(e.target.files, camera.current)} />
      <input ref={library} type="file" accept="image/*" multiple className="hidden"
             aria-label="upload a photo of this product"
             onChange={(e) => void take(e.target.files, library.current)} />
      {error && <p className="text-red-400 text-xs" role="alert">{error}</p>}
    </div>
  );
}
